import { useCallback, useContext, useMemo, useState } from 'react';

import {
  Props,
  SectionState
} from '@/components/EpisodeOfCareSection/components/EnrollOrReject/types';
import { Color } from '@/constants/color';
import EnrollmentForm from '@/forms/EnrollmentForm';
import RejectionForm from '@/forms/RejectionForm';
import PersonContext from '@/pages/Person/PersonContext';
import personNeedsNewProgramQualification from '@/pages/Profile/utils/personNeedsNewProgramQualification';
import ProgramRepository from '@/repositories/ProgramRepository';
import Button from '@/ui/components/Button';
import Spinner from '@/ui/components/Spinner';

const EnrollOrReject = ({ episodeOfCare, programQualification }: Props) => {
  const { refetch: refetchPerson, person } = useContext(PersonContext);
  const [sectionState, setSectionState] = useState<SectionState>(SectionState.VIEWING);
  const [creatingProgramQualification, setCreatingProgramQualification] = useState(false);
  const [fetchingPersonData, setFetchingPersonData] = useState(false);
  const programRepository = ProgramRepository();

  const createProgramQualification = useCallback(async () => {
    setCreatingProgramQualification(true);
    const newProgramQualification = await programRepository.createProgramQualification({
      params: {
        person_id: person.id,
        primary_substance: programQualification?.primary_substance ?? null,
        _program_id: programQualification?.selected_program?.id ?? null
      }
    });
    setCreatingProgramQualification(false);

    return newProgramQualification;
  }, [
    person.id,
    programQualification?.primary_substance,
    programQualification?.selected_program?.id,
    programRepository
  ]);

  const needsToCreateNewProgramQualification = useMemo(() => {
    return personNeedsNewProgramQualification(person);
  }, [person]);

  const handleEnrollClick = useCallback(async () => {
    if (needsToCreateNewProgramQualification) {
      await createProgramQualification();
    }

    // Always refetch to ensure fresh data
    setFetchingPersonData(true);
    await refetchPerson();
    setFetchingPersonData(false);
    setSectionState(SectionState.ENROLLING);
  }, [createProgramQualification, needsToCreateNewProgramQualification, refetchPerson]);

  const handleRejectClick = useCallback(async () => {
    if (needsToCreateNewProgramQualification) {
      await createProgramQualification();

      // refetch only needed when creating new ProgramQualification
      setFetchingPersonData(true);
      await refetchPerson();
      setFetchingPersonData(false);
    }

    setSectionState(SectionState.REJECTING);
  }, [createProgramQualification, needsToCreateNewProgramQualification, refetchPerson]);

  const disableButton = creatingProgramQualification || fetchingPersonData;

  const renderSection = useCallback(() => {
    if (sectionState === SectionState.VIEWING) {
      return (
        <div className="space-x-1">
          <Button
            color={Color.EMERALD}
            disabled={disableButton}
            loading={disableButton}
            onClick={handleEnrollClick}
            size="sm"
            type="button">
            {disableButton ? <Spinner /> : 'Enroll in Program'}
          </Button>

          <Button
            color={Color.RED}
            disabled={disableButton}
            loading={disableButton}
            onClick={handleRejectClick}
            size="sm"
            type="button">
            {disableButton ? <Spinner /> : 'Reject from Program'}
          </Button>
        </div>
      );
    }

    if (programQualification) {
      if (sectionState === SectionState.ENROLLING) {
        return (
          <div>
            <EnrollmentForm
              episodeOfCare={episodeOfCare}
              personId={person.id}
              programQualificationId={programQualification?.id}
              onSuccess={() => setSectionState(SectionState.VIEWING)}
            />

            <Button color={Color.WHITE} onClick={() => setSectionState(SectionState.VIEWING)}>
              Cancel
            </Button>
          </div>
        );
      }

      if (sectionState === SectionState.REJECTING) {
        return (
          <div>
            <RejectionForm
              personId={person.id}
              programQualificationId={programQualification?.id}
              onSuccess={() => setSectionState(SectionState.VIEWING)}
            />

            <Button color={Color.WHITE} onClick={() => setSectionState(SectionState.VIEWING)}>
              Cancel
            </Button>
          </div>
        );
      }
    }
  }, [
    disableButton,
    episodeOfCare,
    handleEnrollClick,
    handleRejectClick,
    person.id,
    programQualification,
    sectionState
  ]);

  return <div>{renderSection()}</div>;
};

export default EnrollOrReject;
